import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * AppShell — the grid that holds Sidebar + TopHeader + Main + optional Inspector.
 * Pencil: sUC0D/nvFxF (Dark) + IIQUl/g4EZn (Light).
 *
 * CSS Grid layout:
 *   grid-template-columns: var(--sidebar-w) 1fr auto
 *   grid-template-rows:    64px 1fr
 *
 *   [ sidebar ] [ top header ............. ]
 *   [         ] [ main content | inspector ]
 *
 * Children slots:
 *   sidebar   — any Sidebar element (controls its own width)
 *   header    — TopHeader element
 *   main      — page content (scrollable)
 *   inspector — optional right-docked panel (fixed 420w by default)
 */
export interface AppShellProps extends React.HTMLAttributes<HTMLDivElement> {
  sidebar: React.ReactNode
  header?: React.ReactNode
  inspector?: React.ReactNode
  inspectorWidth?: number
}

export const AppShell = React.forwardRef<HTMLDivElement, AppShellProps>(
  ({ className, sidebar, header, inspector, inspectorWidth = 420, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('h-dvh w-full bg-background text-ia-text flex', className)}
      {...props}
    >
      {sidebar}
      <div className="flex flex-col flex-1 min-w-0">
        {header}
        <div className="flex flex-1 min-h-0">
          <main className="flex-1 overflow-y-auto overflow-x-auto">{children}</main>
          {inspector && (
            <aside
              aria-label="Inspector panel"
              style={{ width: inspectorWidth }}
              className="shrink-0 border-l border-ia-border bg-card overflow-y-auto"
            >
              {inspector}
            </aside>
          )}
        </div>
      </div>
    </div>
  ),
)
AppShell.displayName = 'AppShell'

/**
 * PageShell — the inner wrapper every route uses inside <AppShell main>.
 * Provides consistent page-level padding + max-width guardrails + slot for PageHeader.
 */
export interface PageShellProps extends React.HTMLAttributes<HTMLDivElement> {
  header?: React.ReactNode
  /** When true, content uses full width; default caps at 1920 for readability. */
  wide?: boolean
}

export const PageShell = React.forwardRef<HTMLDivElement, PageShellProps>(
  ({ className, header, wide, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col gap-6 px-6 py-6',
        !wide && 'max-w-[1920px] mx-auto w-full',
        className,
      )}
      {...props}
    >
      {header}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  ),
)
PageShell.displayName = 'PageShell'
